import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from '../UserContext';
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "./index.css";

const formatarTamanho = (bytes) => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const formatarData = (mtime) => {
  if (!mtime) return "";
  const d = new Date(mtime);
  return d.toLocaleString("pt-BR");
};

export default function ModalXML({ onClose }) {
  const { userInfo } = useUser();
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [selecionados, setSelecionados] = useState(new Set());
  const [baixandoZip, setBaixandoZip] = useState(false);
  const [progresso, setProgresso] = useState(0);

  const baseUrl = `https://api.wensystem.com.br/arquivos/${userInfo.alias}/NFCe/XML/`;

  const fetchArquivos = useCallback(async () => {
    try {
      setLoading(true);
      setErro("");

      const res = await fetch(baseUrl, {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        setErro("Erro ao listar arquivos.");
        return;
      }

      const json = await res.json();
      const apenasXml = json
        .filter((f) => f.type === "file" && f.name.toLowerCase().endsWith(".xml"))
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime));

      setArquivos(apenasXml);
    } catch (err) {
      console.error("Erro ao buscar XMLs:", err);
      setErro("Erro ao listar arquivos.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchArquivos();
  }, [fetchArquivos]);

  const extrairDataISO = (mtime) => {
    if (!mtime) return "";
    const d = new Date(mtime);
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, "0");
    const dia = String(d.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  };

  const arquivosFiltrados = useMemo(() => {
    let resultado = arquivos;

    if (busca.trim()) {
      const termo = busca.trim().toLowerCase();
      resultado = resultado.filter((f) => f.name.toLowerCase().includes(termo));
    }

    if (dataInicio) {
      resultado = resultado.filter((f) => extrairDataISO(f.mtime) >= dataInicio);
    }

    if (dataFim) {
      resultado = resultado.filter((f) => extrairDataISO(f.mtime) <= dataFim);
    }

    return resultado;
  }, [arquivos, busca, dataInicio, dataFim]);

  const toggleSelecionado = (nome) => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(nome)) novo.delete(nome);
      else novo.add(nome);
      return novo;
    });
  };

  const selecionarTodos = () => {
    if (selecionados.size === arquivosFiltrados.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(arquivosFiltrados.map((f) => f.name)));
    }
  };

  // ---------- Download individual ----------
  const baixarArquivo = async (nome) => {
    try {
      const res = await fetch(`${baseUrl}${nome}`);
      const blob = await res.blob();
      saveAs(blob, nome);
    } catch (err) {
      console.error("Erro ao baixar arquivo:", err);
      alert("Erro ao baixar o arquivo.");
    }
  };

  // ---------- Download em lote (ZIP) ----------
  const baixarSelecionadosZip = async () => {
    if (selecionados.size === 0) return;

    try {
      setBaixandoZip(true);
      setProgresso(0);

      const zip = new JSZip();
      const nomes = Array.from(selecionados);

      for (let i = 0; i < nomes.length; i++) {
        const nome = nomes[i];
        const res = await fetch(`${baseUrl}${nome}`);
        const blob = await res.blob();
        zip.file(nome, blob);
        setProgresso(Math.round(((i + 1) / nomes.length) * 100));
      }

      const conteudo = await zip.generateAsync({ type: "blob" });
      const dataAtual = new Date().toISOString().split("T")[0];
      saveAs(conteudo, `xmls_${dataAtual}.zip`);
    } catch (err) {
      console.error("Erro ao gerar ZIP:", err);
      alert("Erro ao gerar o arquivo ZIP.");
    } finally {
      setBaixandoZip(false);
      setProgresso(0);
    }
  };

  const limparFiltroData = () => {
    setDataInicio("");
    setDataFim("");
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box modal-xml">
        <div className="modal-header">
          <h3>Download de XML (NFC-e)</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="xml-toolbar">
          <input
            type="text"
            className="xml-busca"
            placeholder="Buscar por nome do arquivo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />

          <div className="xml-filtro-data">
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              title="Data início"
            />
            <span>até</span>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              title="Data fim"
            />
            {(dataInicio || dataFim) && (
              <button
                className="btn-limpar-data"
                onClick={limparFiltroData}
                title="Limpar filtro de data"
              >
                ✕
              </button>
            )}
          </div>

          <div className="xml-toolbar-actions">
            <button className="btn-refresh" onClick={fetchArquivos}>
              ⟳ Atualizar
            </button>
            <button
              className="btn-selecionar-todos"
              onClick={selecionarTodos}
              disabled={arquivosFiltrados.length === 0}
            >
              {selecionados.size === arquivosFiltrados.length && arquivosFiltrados.length > 0
                ? "Desmarcar todos"
                : "Selecionar todos"}
            </button>
          </div>
        </div>

        {erro && <div className="xml-erro">{erro}</div>}
        {loading && <div className="xml-loading">Carregando arquivos...</div>}

        <div className="xml-lista-wrapper">
          {arquivosFiltrados.length === 0 && !loading ? (
            <div className="xml-empty">Nenhum arquivo XML encontrado.</div>
          ) : (
            <ul className="xml-lista">
              {arquivosFiltrados.map((f) => (
                <li
                  key={f.name}
                  className={`xml-item ${selecionados.has(f.name) ? "selecionado" : ""}`}
                >
                  <label className="xml-checkbox">
                    <input
                      type="checkbox"
                      checked={selecionados.has(f.name)}
                      onChange={() => toggleSelecionado(f.name)}
                    />
                  </label>

                  <div className="xml-info" onClick={() => toggleSelecionado(f.name)}>
                    <span className="xml-nome" title={f.name}>{f.name}</span>
                    <span className="xml-meta">
                      {formatarData(f.mtime)} • {formatarTamanho(f.size)}
                    </span>
                  </div>

                  <button
                    className="btn-download-individual"
                    onClick={() => baixarArquivo(f.name)}
                    title="Baixar este arquivo"
                  >
                    ⬇
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="xml-footer">
          <span className="xml-selecionados-count">
            {selecionados.size} arquivo(s) selecionado(s)
          </span>

          <button
            className="btn-baixar-zip"
            onClick={baixarSelecionadosZip}
            disabled={selecionados.size === 0 || baixandoZip}
          >
            {baixandoZip ? `Gerando ZIP... ${progresso}%` : `⬇ Baixar Selecionados (.zip)`}
          </button>
        </div>
      </div>
    </div>
  );
}